// Cascade paths: what deleting one row of the selected entity does (`deleteImpact` in core), drawn
// over the diagram as it is. Nothing is hidden or moved: the cards the delete reaches are tagged with
// what the database does to their rows, and the lines it travels are colored by what happens along
// them. A join table drawn as a many-to-many line loses its rows along that line.
import type { DeleteEffect, DeleteImpact } from '@modelviewer/core';
import type { Graph, GraphEdge } from './graph';

export interface DeletionOverlay {
  impact: DeleteImpact;
  /** The diagram with the effects on its cards and lines; what the delete leaves alone keeps its objects. */
  graph: Graph;
  /** Cards and lines the delete reaches: the start, the base types it goes through, and the lines between. */
  nodes: Set<string>;
  edges: Set<string>;
  /** The line each step's relationship is drawn as: its own, or a collapsed join table's. */
  lineOf: Map<number, string>;
}

/** Strongest first: a card whose rows are deleted is drawn as deleted, whatever else happens to it. */
export const EFFECTS: DeleteEffect[] = ['delete', 'block', 'setNull'];

export function deletionOverlay(graph: Graph, impact: DeleteImpact): DeletionOverlay {
  const cards = new Set(graph.nodes.map((n) => n.id));
  const nodes = new Set([impact.start]);
  const effects = new Map<string, Set<DeleteEffect>>();
  const lineOf = new Map<number, string>();
  const lineEffects = new Map<string, Set<DeleteEffect>>();
  for (const s of impact.steps) {
    nodes.add(s.entity);
    nodes.add(s.principalEntity);
    effects.set(s.entity, (effects.get(s.entity) ?? new Set()).add(s.effect));
    const line = cards.has(s.entity) ? `rel:${s.relationship}` : `m2m:${s.entity}`;
    lineOf.set(s.relationship, line);
    lineEffects.set(line, (lineEffects.get(line) ?? new Set()).add(s.effect));
  }
  // SQL Server's refusals, on the lines that make them.
  const conflicts = new Map<string, string>();
  for (const c of impact.conflicts) {
    for (const i of c.steps) conflicts.set(lineOf.get(impact.steps[i]!.relationship)!, c.cycle ? 'SQL Server: cycle' : `SQL Server: ${c.steps.length} paths`);
  }

  const edges = new Set(lineEffects.keys());
  const shownEdges: GraphEdge[] = graph.edges.map((e) => {
    // Inheritance between the cards reached says why a base type is among them.
    if (e.kind === 'inheritance' && nodes.has(e.source) && nodes.has(e.target)) edges.add(e.id);
    const own = lineEffects.get(e.id);
    if (!own) return e;
    const conflict = conflicts.get(e.id);
    return { ...e, effect: EFFECTS.find((x) => own.has(x))!, ...(conflict ? { conflict } : {}) };
  });
  const shownNodes = graph.nodes.map((n) => {
    const own = effects.get(n.id);
    const start = n.id === impact.start;
    if (!own && !start) return n;
    return { ...n, ...(own ? { effects: EFFECTS.filter((e) => own.has(e)) } : {}), ...(start ? { deleteStart: true } : {}) };
  });
  return { impact, graph: { nodes: shownNodes, edges: shownEdges }, nodes: new Set([...nodes].filter((id) => cards.has(id))), edges, lineOf };
}

/** The lines and cards along the paths of these steps (indexes into `impact.steps`), from the start. */
export function pathsOf(overlay: DeletionOverlay, steps: number[]): { nodes: Set<string>; edges: Set<string> } {
  const { impact, lineOf } = overlay;
  const byRelationship = new Map(impact.steps.map((s) => [s.relationship, s]));
  const nodes = new Set([impact.start]);
  const edges = new Set<string>();
  for (const i of steps) {
    for (const r of impact.steps[i]?.path ?? []) {
      const s = byRelationship.get(r)!;
      edges.add(lineOf.get(r)!);
      nodes.add(s.principalEntity);
      nodes.add(s.entity);
    }
  }
  return { nodes, edges };
}
